import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Offcanvas } from 'react-bootstrap'
import { format } from 'date-fns'

import { GET } from 'helpers/api'

import {
	CURRENCY_SIGNS,
	calcDifference,
	currencyOf,
	formatAmountInput,
	formatSum,
	isEmptyAmount,
	roundAmount,
	sanitizeAmountInput,
} from './shiftClose/shiftCloseCalc'
import './shiftClose/shiftClose.css'

function formatDateTime(value) {
	return value ? format(new Date(value), 'dd.MM.yyyy HH:mm') : '—'
}

// "Приём в сейф" side panel: pending -> enter accepted amounts, accepted -> read only
// item = SafeDepositDTO from the list; on open GET /safe-deposits/{id}/acceptance-details
// (SafeDepositsAcceptanceDetailsDTO), the list item is shown until it arrives
function SafeAcceptPanel({ item, isPending, onHide, onAccept }) {
	const { t } = useTranslation()

	const [accepted, setAccepted] = useState({})
	const [details, setDetails] = useState(null)

	const info = details || item
	const amounts = details?.safeDepositAcceptanceDetailsAmounts || item?.safeDepositAmounts || []
	const rows = amounts.map(amount => ({
		key: amount.detailId,
		name: [amount.paymentTypeName, amount.currencyName].filter(Boolean).join(' · '),
		currency: currencyOf(amount),
		handed: amount.sentAmount,
		accepted: amount.acceptedAmount,
	}))
	const incomplete = rows.some(row => isEmptyAmount(accepted[row.key]))
	const safeTransferId = item?.safeTransferId

	useEffect(() => {
		setAccepted({})
		setDetails(null)
		if (!safeTransferId) return

		// ignores a late response after the panel was closed / switched to another item
		let active = true
		GET(`/services/web/api/safe-deposits/${safeTransferId}/acceptance-details`, {}, { loader: true }).then(response => {
			if (active && response) setDetails(response)
		})
		return () => { active = false }
	}, [safeTransferId])

	// UpdateSafeTransferAmountDetailsDTO list, detailId comes from acceptance-details
	function submit() {
		onAccept(safeTransferId, rows.map(row => ({
			detailId: row.key,
			acceptedAmount: roundAmount(accepted[row.key], row.currency),
		})))
	}

	function fillHanded() {
		const next = {}
		rows.forEach(row => { next[row.key] = String(roundAmount(row.handed, row.currency)) })
		setAccepted(next)
	}

	function renderDifference(row, value) {
		if (isEmptyAmount(value)) return <span className="sc-muted-dash">—</span>
		const difference = calcDifference(value, row.handed, row.currency)
		if (difference === 0) return <b>{formatSum(0, row.currency)}</b>
		return <b className={difference < 0 ? 'sc-diff-shortage' : 'sc-diff-surplus'}>{difference > 0 && '+'}{formatSum(difference, row.currency)}</b>
	}

	return (
		<Offcanvas show={!!item} onHide={onHide} placement="end" backdrop="static" className="sc-panel">
			<Offcanvas.Header closeButton>
				<Offcanvas.Title>{t('SAFE_ACCEPTANCE')}</Offcanvas.Title>
			</Offcanvas.Header>

			<Offcanvas.Body>
				<div className="sc-info-row">
					<div className="sc-info">
						<span>{t('TRADING_POINT')}</span><b>{info?.posName}</b>
						<span>{t('CASHBOX')}</span><b>{info?.cashboxName}</b>
						<span>{t('SHIFT')}</span><b>№{info?.shiftNumber}</b>
						<span>{t('HANDED_BY')}</span><b>{info?.senderName}</b>
						<span>{t('CLOSE_DATE')}</span><b>{formatDateTime(info?.cashboxCloseDate)}</b>
					</div>
					{!isPending &&
						<div className="sc-info">
							<span>{t('ACCEPTED_BY')}</span><b>{item?.receiverName || '—'}</b>
						</div>
					}
				</div>

				<div className="sc-grid sc-grid-safe">
					<div className="sc-grid-head">
						<div>{t('SC_PAYMENT_TYPE')}</div>
						<div className="text-end">{t('HANDED')}</div>
						{isPending ?
							<div className="sc-head-with-btn">
								<button type="button" className="sc-icon-btn" title={t('SC_FILL_EXPECTED')} onClick={fillHanded}>
									<i className="uil uil-arrow-to-right"></i>
								</button>
								{t('ACCEPTED_AMOUNT')}
							</div>
							:
							<div className="text-end">{t('ACCEPTED_AMOUNT')}</div>
						}
						<div className="text-end">{t('SC_DIFFERENCE')}</div>
					</div>

					{rows.map(row => (
						<div className="sc-grid-row" key={row.key}>
							<div className="sc-row-name">
								<i className="uil uil-money-bill"></i>
								{row.name}
							</div>
							<div className="sc-amount">{formatSum(row.handed, row.currency)}</div>
							{isPending ?
								<div className="sc-input-wrap">
									<input
										type="text"
										inputMode="decimal"
										className="form-control"
										placeholder="0"
										value={formatAmountInput(accepted[row.key])}
										onChange={(e) => setAccepted({ ...accepted, [row.key]: sanitizeAmountInput(e.target.value) })}
									/>
									<span className="sc-input-suffix">{CURRENCY_SIGNS[row.currency]}</span>
								</div>
								:
								<div className="sc-amount">{formatSum(row.accepted, row.currency)}</div>
							}
							<div className="text-end">{renderDifference(row, isPending ? accepted[row.key] : row.accepted)}</div>
						</div>
					))}
				</div>
			</Offcanvas.Body>

			<div className="sc-footer">
				<div className="sc-footer-right">
					{isPending ?
						<>
							<button type="button" className="btn sc-btn-cancel" onClick={onHide}>{t('CANCEL')}</button>
							<button type="button" className="btn sc-btn-primary" disabled={incomplete || !details} onClick={submit}>
								<i className="uil uil-check"></i>
								{t('ACCEPT')}
							</button>
						</>
						:
						<button type="button" className="btn sc-btn-cancel" onClick={onHide}>{t('CLOSE_CASHBOX')}</button>
					}
				</div>
			</div>
		</Offcanvas>
	)
}

export default SafeAcceptPanel
